import React, { useState, useEffect } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faEdit, faUserSecret } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import SideBar from "./SideBar";

const API_URL = process.env.REACT_APP_API_URL;

const UserDashboard = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const storedRole = await localforage.getItem("role");
        const token = await localforage.getItem("token");

        if (storedRole) {
          setRole(storedRole);

          if (storedRole === "admin" || storedRole === "mod") {
            const response = await api.get(`${API_URL}/auth/users`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            const usersData = response.data;
            setUsers(usersData);
            setFilteredUsers(usersData);
          }
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, []);

  useEffect(() => {
    let filtered = users;

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.roles.some((r) => r.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      const token = await localforage.getItem("token");
      await api.delete(`${API_URL}/auth/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers((prevUsers) => prevUsers.filter((user) => user._id !== userId));
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Failed to delete user");
    }
  };

  const handleUpdateUser = (userId) => {
    navigate(`/update-user/${userId}`);
  };

  const handleUpdateUserPassword = (userId) => {
    navigate(`/update-user-password/${userId}`);
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div className="spinner"></div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SideBar />
        <div className="dashboard-container">
          <div className="alert alert-error">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">User Management</h1>
          <p className="dashboard-subtitle">Manage user accounts and permissions</p>
        </div>

        {(role === "admin" || role === "mod") && (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="card-title">Total Users: {filteredUsers.length}</h3>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/nWuRGm1GvLXyCmQ6TbxqfQ7YasvDlY8z87TxUHrX0HUhX0Pxa9")}
                  >
                    + Add New User
                  </button>
                </div>
              </div>
            </div>

            <div className="data-table-container">
              <div className="data-table-header">
                <h3 className="data-table-title">User List</h3>
                <div className="data-table-actions">
                  <input
                    type="text"
                    placeholder="Search by name, email or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="data-table-search"
                  />
                </div>
              </div>

              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Date Of Birth</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <tr key={user._id}>
                          <td>{user.userName || "N/A"}</td>
                          <td>{user.email}</td>
                          <td style={{ maxWidth: "300px" }}>
                            {user.roles
                              .map((role) => role.charAt(0).toUpperCase() + role.slice(1))
                              .join(", ")}
                          </td>
                          <td>{user.dob}</td>
                          <td>
                            <button
                              onClick={() => handleUpdateUser(user._id)}
                              className="table-action-btn table-action-btn-edit"
                              title="Edit User"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              onClick={() => handleUpdateUserPassword(user._id)}
                              className="table-action-btn table-action-btn-view"
                              title="Change Password"
                            >
                              <FontAwesomeIcon icon={faUserSecret} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="table-action-btn table-action-btn-delete"
                              title="Delete User"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          No users found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UserDashboard;
